import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";

const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];

const DEFAULT_FORM_DATA = {
  issueNo: "",
  scopeOfAppointment: "",
  apName: "",
  apSignature: "",
  apDate: "",
  aeName: "",
  aeSignature: "",
  aeDate: "",
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    issueNo: src.issueNo ?? src.issue_no ?? "",
    scopeOfAppointment: src.scopeOfAppointment ?? src.scope_of_appointment ?? "",
    apName: src.apName ?? src.ap_name ?? "",
    apSignature: src.apSignature ?? src.ap_signature ?? "",
    apDate: src.apDate ?? src.ap_date ?? "",
    aeName: src.aeName ?? src.ae_name ?? "",
    aeSignature: src.aeSignature ?? src.ae_signature ?? "",
    aeDate: src.aeDate ?? src.ae_date ?? "",
  };
}

function toSignatureSrc(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

function SignaturePreview({ value, alt }) {
  const src = toSignatureSrc(value);
  if (!src) return <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>;
  return <img src={src} alt={alt} style={{ maxWidth: "100%", maxHeight: 64, objectFit: "contain", display: "block" }} />;
}

function Sf009MAcceptanceLvElectricalSafetyRulesPage() {
  const canEditDelete = canEditDeleteRecords();
  const isAdminUser = adminIsPrimaryAdmin();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-m-acceptance-lv-electrical-safety-rules", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseSf009DateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.issueNo, fd.apName, fd.aeName, f.name].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      next.formData[key] = value;
      next.name = next.formData.apName || "";
      next.date = next.formData.apDate || "";
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeRecordIntoDefaults(raw),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = mergeRecordIntoDefaults(editForm.formData || {});
      const res = await fetch(getBaseUrl() + "/api/sf-009-m-acceptance-lv-electrical-safety-rules/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          name: raw.apName,
          date: raw.apDate,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-m-acceptance-lv-electrical-safety-rules/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;
    const raw = mergeRecordIntoDefaults(getRawFormData(record));
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const tableMargin = { left: contentX, right: contentX };
    const pendingSignaturePromises = [];
    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseSf009DateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };
    let yPosition = await addLogoAndFormHeader(doc, "Acceptance of LV Electrical Safety Rules");

    const pdfGrid = {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
    };
    const pdfLbl = { fillColor: THEME_LIGHT_RGB, fontStyle: "bold", textColor: THEME_RGB };
    const pdfVal = { fillColor: PDF_WHITE, textColor: [0, 0, 0] };
    const addSignatureToPDF = (signatureData, x, y, maxWidth, maxHeight, pageNumber) =>
      new Promise((resolve) => {
        const src = toSignatureSrc(signatureData);
        if (!src) {
          resolve();
          return;
        }
        try {
          const img = new Image();
          img.onload = () => {
            try {
              const imgW = img.width || maxWidth;
              const imgH = img.height || maxHeight;
              const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);
              const w = Math.max(4, imgW * ratio);
              const h = Math.max(4, imgH * ratio);
              doc.setPage(pageNumber);
              doc.addImage(src, "PNG", x + (maxWidth - w) / 2, y + (maxHeight - h) / 2, w, h);
            } catch {}
            resolve();
          };
          img.onerror = () => resolve();
          img.src = src;
        } catch {
          resolve();
        }
      });

    const pdfHdr = { fillColor: THEME_LIGHT_RGB, fontStyle: "bold", fontSize: 8, textColor: THEME_RGB };
    const pdfTxt = { fillColor: PDF_WHITE, valign: "top", textColor: [0, 0, 0] };

    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      body: [[{ content: "Acknowledgement", styles: { halign: "center", ...pdfHdr, fontSize: 11 } }]],
      styles: pdfGrid,
    });
    yPosition = doc.lastAutoTable.finalY;
    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      body: [
        ["I acknowledge I have received training on and am in receipt of SP-009 LV Electrical Safety Rules."],
        [`SP-009: ${raw.issueNo || "Enter Issue No."}`],
        ["I confirm I have read the documents, understand them and will implement them.  I confirm I understand the scope of my appointment."],
      ],
      columnStyles: { 0: { ...pdfTxt, fontSize: 8.5 } },
      styles: pdfGrid,
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      body: [
        ["Scope of Appointment:", raw.scopeOfAppointment || ""],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.35, ...pdfLbl, valign: "top" },
        1: { cellWidth: contentW * 0.65, ...pdfVal, minCellHeight: 42, valign: "top" },
      },
      styles: { ...pdfGrid, fontSize: 8.5 },
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      body: [
        ["AP (E) Name:", raw.apName || ""],
        ["Signature:", ""],
        ["Date:", safePdfDate(raw.apDate)],
        ["AE Name:", raw.aeName || ""],
        ["Signature:", ""],
        ["Date:", safePdfDate(raw.aeDate)],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.35, ...pdfLbl },
        1: { cellWidth: contentW * 0.65, ...pdfVal, minCellHeight: 18 },
      },
      styles: { ...pdfGrid, fontSize: 8.5, minCellHeight: 9 },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 1) return;
        if (data.row.index === 1) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(
            addSignatureToPDF(raw.apSignature, data.cell.x + 2, data.cell.y + 2, data.cell.width - 4, data.cell.height - 4, pageNumber)
          );
        }
        if (data.row.index === 4) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(
            addSignatureToPDF(raw.aeSignature, data.cell.x + 2, data.cell.y + 2, data.cell.width - 4, data.cell.height - 4, pageNumber)
          );
        }
      },
    });

    await Promise.all(pendingSignaturePromises);
    applyFooterAllPages(doc);
    const safeName = String(raw.apName || "AP").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(formatDate(raw.apDate) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("M", getSf009SharePointFields("M", raw, record, getUserName))
        : `SF009M_${safeName}_${safeDate}.pdf`;
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.apName || ""}|${raw.apDate || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-M Acceptance of LV Electrical Safety Rules PDF generated successfully!");
    }
  };

  const fd = editForm?.formData || DEFAULT_FORM_DATA;

  const renderForm = () => (
    <div style={{ border: `1px solid ${THEME_BORDER}`, background: "#fff" }}>
      <div style={{ textAlign: "center", padding: "14px 12px", fontWeight: 700, fontSize: 15, color: "#1a202c", borderBottom: `1px solid ${THEME_BORDER}`, background: "#fff" }}>
        Acceptance of LV Electrical Safety Rules
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <tbody>
          <tr>
            <td colSpan={2} style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 700, fontSize: 18, padding: "8px 10px", background: THEME_LIGHT, color: THEME }}>
              Acknowledgement
            </td>
          </tr>
          <tr><td colSpan={2} style={{ border: `1px solid ${THEME_BORDER}`, padding: "8px 10px", fontSize: 12 }}>I acknowledge I have received training on and am in receipt of SP-009 LV Electrical Safety Rules.</td></tr>
          <tr>
            <td colSpan={2} style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12 }}>
              SP-009: <input type="text" value={fd.issueNo || ""} onChange={(e) => updateFormData("issueNo", e.target.value)} style={{ border: "none", outline: "none", width: "70%" }} placeholder="Enter Issue No." />
            </td>
          </tr>
          <tr><td colSpan={2} style={{ border: `1px solid ${THEME_BORDER}`, padding: "8px 10px", fontSize: 12 }}>I confirm I have read the documents, understand them and will implement them.  I confirm I understand the scope of my appointment.</td></tr>
          <tr>
            <td style={{ border: `1px solid ${THEME_BORDER}`, width: "35%", verticalAlign: "top", padding: "8px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>Scope of Appointment:</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, width: "65%", padding: 0 }}>
              <textarea value={fd.scopeOfAppointment || ""} onChange={(e) => updateFormData("scopeOfAppointment", e.target.value)} style={{ width: "100%", minHeight: 170, border: "none", outline: "none", resize: "vertical", padding: 8, fontSize: 12 }} />
            </td>
          </tr>
          <tr><td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>AP (E) Name:</td><td style={{ border: `1px solid ${THEME_BORDER}`, padding: 4 }}><input type="text" value={fd.apName || ""} onChange={(e) => updateFormData("apName", e.target.value)} style={{ width: "100%", border: "none", outline: "none", fontSize: 12 }} /></td></tr>
          <tr><td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>Signature:</td><td style={{ border: `1px solid ${THEME_BORDER}`, padding: 4, background: "#fff" }}><div style={{ minHeight: 64, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.8 }}><SignaturePreview value={fd.apSignature} alt="AP signature" /></div></td></tr>
          <tr><td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>Date:</td><td style={{ border: `1px solid ${THEME_BORDER}`, padding: 4 }}><DatePicker selected={parseSf009DateForPicker(fd.apDate)} onChange={(d) => updateFormData("apDate", d ? datePickerToSf009DateString(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0 p-0" placeholderText="dd-MM-yyyy" wrapperClassName="w-100" /></td></tr>
          <tr><td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>AE Name:</td><td style={{ border: `1px solid ${THEME_BORDER}`, padding: 4 }}><input type="text" value={fd.aeName || ""} onChange={(e) => updateFormData("aeName", e.target.value)} style={{ width: "100%", border: "none", outline: "none", fontSize: 12 }} /></td></tr>
          <tr><td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>Signature:</td><td style={{ border: `1px solid ${THEME_BORDER}`, padding: 4, background: "#fff" }}><div style={{ minHeight: 64, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.8 }}><SignaturePreview value={fd.aeSignature} alt="AE signature" /></div></td></tr>
          <tr><td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 10px", fontSize: 12, fontWeight: 600, background: THEME_LIGHT, color: THEME }}>Date:</td><td style={{ border: `1px solid ${THEME_BORDER}`, padding: 4 }}><DatePicker selected={parseSf009DateForPicker(fd.aeDate)} onChange={(d) => updateFormData("aeDate", d ? datePickerToSf009DateString(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0 p-0" placeholderText="dd-MM-yyyy" wrapperClassName="w-100" /></td></tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-M Acceptance of LV Electrical Safety Rules</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && <button type="button" onClick={() => setShowImportModal(true)} style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Import File</button>}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input type="text" placeholder="Type to Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }} />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Issue No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>AP (E) Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>AE Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>AP Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.issueNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.apName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.aeName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(raw.apDate || r.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} /></button>}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} /></button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.apName || ""}|${raw.apDate || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(26, 32, 44, 0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto", border: "none", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(139, 22, 52, 0.15)" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>Edit SF-009-M Acceptance of LV Electrical Safety Rules</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "20px 24px 8px", background: "#fff" }}>{renderForm()}</div>
              <div className="modal-footer" style={{ background: "#fff", borderTop: `1px solid ${THEME_BORDER}`, padding: "14px 24px", gap: 10 }}>
                <button type="button" className="btn btn-light" style={{ borderRadius: 8, fontWeight: 600, border: `1px solid ${THEME_BORDER}` }} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 22px", border: "none" }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-M Acceptance of LV Electrical Safety Rules File"
        importPath="sf-009-m-acceptance-lv-electrical-safety-rules/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-M Acceptance of LV Electrical Safety Rules</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009MAcceptanceLvElectricalSafetyRulesPage;
